import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Difference = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRP vs. IFTA: Key Differences for Trucking Compliance"
          description="Understand the differences between IRP and IFTA. Learn who needs them, how to register, and why compliance is essential for commercial truck operators."
          keywords=""
          canonical="resources/excise-tax/difference-between-irp-vs-ifta/"
          ogType="article"
          // jsonLdPricing="11.99"
          // jsonLdAggregateRatingValue="4.95"
          // jsonLdAggregateRatingCount="4278"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          Understanding the key Differences:{' '}
          <span className="highlight-container">
            <span className="highlight">IRP vs. IFTA </span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Stay ahead of compliance and keep your trucks on the road
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=differences`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/excise-tax/difference-between-irp-vs-ifta/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-10T09:00:00-05:00"
                      >
                        August 10, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-10T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        August 10, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    IRP vs IFTA at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          IRP (International Registration Plan)
                        </strong>{' '}
                        and{' '}
                        <strong style={{ fontWeight: 600 }}>
                          IFTA (International Fuel Tax Agreement)
                        </strong>{' '}
                        are multi-jurisdiction programs that help commercial
                        carriers operating across U.S. states and Canadian
                        provinces manage vehicle registration and fuel tax
                        obligations.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          IRP Purpose:
                        </strong>{' '}
                        IRP simplifies commercial vehicle registration by
                        allowing carriers to register qualified vehicles in
                        their base jurisdiction and pay apportioned registration
                        fees based on miles traveled in participating
                        jurisdictions.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          IFTA Purpose:
                        </strong>{' '}
                        IFTA simplifies fuel tax reporting by allowing carriers
                        to file one quarterly fuel tax report with their base
                        jurisdiction, which distributes the applicable taxes to
                        other member jurisdictions.
                      </span>
                    </li>

                    <li>
                      <span>
                        Carriers operating qualified commercial vehicles in{' '}
                        <strong style={{ fontWeight: 600 }}>
                          two or more member jurisdictions
                        </strong>{' '}
                        may need IRP and/or IFTA. This includes vehicles with a
                        gross vehicle weight over{' '}
                        <strong style={{ fontWeight: 600 }}>
                          26,000 pounds
                        </strong>{' '}
                        or{' '}
                        <strong style={{ fontWeight: 600 }}>
                          three or more axles
                        </strong>
                        .
                      </span>
                    </li>

                    <li>
                      <span>
                        IRP registration is generally renewed{' '}
                        <strong style={{ fontWeight: 600 }}>annually</strong>,
                        while IFTA fuel tax returns are generally filed{' '}
                        <strong style={{ fontWeight: 600 }}>quarterly</strong>.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you own or operate commercial trucks, especially across
                  multiple states or provinces, you’ve likely heard of{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    <Link
                      href="/resources/excise-tax/irp/"
                      sx={{ color: '#0202ff' }}
                    >
                      IRP
                    </Link>{' '}
                  </span>
                  (International Registration Plan) and{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    <Link
                      href="/resources/excise-tax/international-fuel-tax-agreement/"
                      sx={{ color: '#0202ff' }}
                    >
                      IFTA
                    </Link>
                  </span>{' '}
                  (International Fuel Tax Agreement). Both are essential for
                  legal interstate and interprovincial trucking operations in
                  North America, but they serve different purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This resource page breaks down the differences between IRP and
                  IFTA, helping trucking companies,{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-filing-guide-for-owner-operators/"
                    sx={{ color: '#0202ff' }}
                  >
                    owner-operators
                  </Link>
                  , and{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-filing-guide-for-fleet-managers/"
                    sx={{ color: '#0202ff' }}
                  >
                    fleet managers
                  </Link>{' '}
                  understand what each program covers, why they matter, and how
                  to comply.
                </Typography>

                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is an IRP?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is IFTA?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Key Differences Between IRP and IFTA
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why are IRP and IFTA Important?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How to Register IRP and IFTA?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whois" sx={{ color: '#0202ff' }}>
                          Who is exempt from IRP and IFTA?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem
                      id="whatis"
                      sx={{ py: 0.5, alignItems: 'flex-start' }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#penalty" sx={{ color: '#0202ff' }}>
                          Penalties for Non-Compliance
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is an IRP?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  The{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    International Registration Plan (IRP)
                  </span>{' '}
                  is a vehicle registration reciprocity agreement among U.S.
                  states and Canadian provinces. It allows commercial motor
                  carriers to register their vehicles in their home jurisdiction
                  and receive apportioned license plates that authorize
                  operation in all participating jurisdictions.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Purpose:</span>{' '}
                    Distributes vehicle registration fees based on the
                    proportion of miles traveled in each jurisdiction.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> How it works:</span>{' '}
                    Carriers obtain a single “apportioned” license plate and cab
                    card from their base jurisdiction. Fees are then shared
                    among all jurisdictions where the vehicle operates,
                    according to the miles driven in each.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }} id="what">
                      {' '}
                      Who needs IRP:
                    </span>{' '}
                    Generally, commercial vehicles over{' '}
                    <span style={{ fontWeight: 600 }}>26,000 pounds</span> gross
                    vehicle weight (GVW) or those with{' '}
                    <span style={{ fontWeight: 600 }}>
                      three or more axles,
                    </span>
                    operating in two or more member jurisdictions
                  </li>
                </Box>
                {/* <Box
                  component={'div'}
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Ready to File and Pay Your Taxes in an Easy Way?
                      </Typography>
                      <Typography variant="body2">
                        Use Electronic Funds Withdrawal (EFW) to file and pay in
                        one simple, secure step.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        id="who"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': { color: '#fff' },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=efw`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  What is IFTA?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The{' '}
                  <span style={{ fontWeight: 600 }}>
                    International Fuel Tax Agreement (IFTA)
                  </span>{' '}
                  is an agreement among U.S. states and Canadian provinces to
                  simplify fuel tax reporting for commercial motor carriers
                  operating in multiple jurisdictions.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Purpose:</span>{' '}
                    Simplifies fuel tax reporting for carriers by consolidating
                    tax payments and filings into a single quarterly report to
                    the base jurisdiction.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> How it works:</span>{' '}
                    Carriers track miles traveled and fuel purchased in each
                    jurisdiction. Taxes are calculated based on where the fuel
                    is used, not just where it was purchased. The base
                    jurisdiction collects the taxes and distributes them to the
                    appropriate states or provinces.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }} id="key">
                      {' '}
                      Who needs IFTA:
                    </span>{' '}
                    Commercial vehicles over{' '}
                    <span style={{ fontWeight: 600 }}>26,000 pounds</span> GVW
                    or with three or more axles, operating in two or more member
                    jurisdictions
                  </li>
                </Box>

                {/* <Alert
                  severity="warning"
                  id="eligible"
                  icon={false}
                  sx={{ mb: 3, mt: 0 }}
                >
                  <b>Note:</b> EFW is not available when filing a paper return.
                </Alert> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Key Differences Between IRP and IFTA
                </Typography>
                <Box sx={{ mb: 5 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 1 }}
                  >
                    <Table aria-label="EFW Individual">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                            }}
                          >
                            Aspect
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                            }}
                          >
                            IRP (International Registration Plan)
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                            }}
                          >
                            IFTA (International Fuel Tax Agreement)
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Purpose{' '}
                          </TableCell>
                          <TableCell>
                            Vehicle registration and apportioned licensing
                          </TableCell>
                          <TableCell>
                            Fuel tax collection and reporting
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            What it covers
                          </TableCell>
                          <TableCell>
                            Registration fees based on vehicle mileage{' '}
                          </TableCell>
                          <TableCell>
                            Fuel taxes based on fuel consumption and mileage
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Who needs it
                          </TableCell>
                          <TableCell>
                            Truck owners/operators with commercial vehicles
                          </TableCell>
                          <TableCell>
                            Truck owners/operators with qualified vehicles
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            How fees/taxes are paid
                          </TableCell>
                          <TableCell>
                            Fees paid to base jurisdiction, apportioned to
                            others
                          </TableCell>
                          <TableCell>
                            {' '}
                            Fuel tax paid to base jurisdiction, apportioned to
                            others
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Reporting frequency
                          </TableCell>
                          <TableCell>Annual vehicle registration</TableCell>
                          <TableCell>Quarterly fuel tax reporting</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Governing authority
                          </TableCell>
                          <TableCell>
                            Jurisdiction transportation departments
                          </TableCell>
                          <TableCell>
                            {' '}
                            Jurisdiction transportation/fuel tax departments
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Required documents
                          </TableCell>
                          <TableCell>
                            Vehicle registration, proof of insurance
                          </TableCell>
                          <TableCell id="why">
                            Fuel purchase records, mileage logs
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Why are IRP and IFTA Important?
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Compliance:</span> Both
                    ensure you comply with multi-jurisdiction laws and avoid
                    fines.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Simplification:</span>{' '}
                    They simplify registration and tax reporting for carriers
                    operating across many states or provinces.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Cost Allocation:</span>{' '}
                    They allocate costs based on actual travel and fuel usage.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }} id="how">
                      {' '}
                      Legal Operation:
                    </span>{' '}
                    Operating without IRP or IFTA when required can lead to
                    penalties and vehicle impoundment.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  How to Register IRP and IFTA?
                </Typography>

                <Box component="ul" sx={{ mb: 3.5, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Determine Your Base Jurisdiction:
                    </span>{' '}
                    Usually your company’s principal place of business.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Gather Required Information:
                    </span>{' '}
                    Vehicle details, mileage logs, fuel receipts.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Apply for IRP Plates:
                    </span>{' '}
                    Submit an IRP application to your base jurisdiction’s motor
                    vehicle department.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Apply for IFTA License:
                    </span>{' '}
                    File IFTA registration with your base jurisdiction’s tax
                    authority.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Maintain Records:</span>{' '}
                    Keep detailed mileage and fuel purchase records for all
                    jurisdictions.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }} id="whois">
                      File Regular Reports:
                    </span>{' '}
                    IRP annually; IFTA quarterly.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Who is Exempt from IRP and IFTA?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  You{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    may not need IRP or IFTA
                  </span>{' '}
                  if:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>You operate solely within one state or province.</li>
                  <li>
                    Your vehicle weighs under 26,000 pounds and has fewer than 3
                    axles.
                  </li>
                  <li>
                    You’re operating a recreational or government-owned vehicle.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Penalties for Non-Compliance
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Failing to comply with IRP or IFTA regulations can result in:
                </Typography>
                <Typography
                  id="when"
                  variant="body1"
                  component="div"
                  sx={{ mb: 4 }}
                >
                  <ul>
                    <li style={{ marginBottom: 16 }}>Fines and penalties</li>
                    <li style={{ marginBottom: 16 }}>Vehicle impoundment</li>
                    <li style={{ marginBottom: 16 }}>
                      Audits and back payments
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Suspended operating privileges
                    </li>
                  </ul>
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information given above is based on official{' '}
                        <Link
                          href="https://www.irponline.org/"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRP guidelines
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.iftach.org/"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IFTA guidelines
                        </Link>
                        , as well as state and provincial motor vehicle and tax
                        authorities.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      {/* <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Don’t Forget to File Form 2290!
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              If your vehicle weighs 55,000 pounds or more, you must <br /> file
              <span style={{ fontWeight: 600 }}> IRS Form 2290</span> and pay
              the{' '}
              <span style={{ fontWeight: 600 }}>
                Heavy Vehicle Use Tax (HVUT)
              </span>
              .
            </Typography>
            <Box
              component={'ul'}
              sx={{ mb: 2, listStyleType: 'none', '& li': { mb: 1.5 } }}
            >
              <li>
                📄 <span style={{ fontWeight: 600 }}>Need help?</span>{' '}
                <Link
                  href="/resources/excise-tax/form-2290-instructions/"
                  sx={{ color: '#0202ff' }}
                >
                  {' '}
                  Check out Form 2290 Instructions
                </Link>
              </li>
              <li>
                🎥{' '}
                <span style={{ fontWeight: 600 }}>
                  Watch this quick video guide:
                </span>{' '}
                <Link
                  href="https://www.youtube.com/watch?v=QnUmGYD2yLw&list=PLmvRKAW8mnOiHgODlf35zeTg7wBnQ1SZg&index=2&pp=iAQB"
                  target="_blank"
                  sx={{ color: '#0202ff' }}
                >
                  {' '}
                  How to File Form 2290
                </Link>{' '}
              </li>
            </Box>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=efw`}
              >
                Start Filing Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container> */}
      <Container maxWidth="lg" sx={{ mb: 8, mt: { xs: 6, sm: 6, md: 6 } }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
            textAlign: 'center',
          }}
        >
          Don’t Forget to File Form 2290!
        </Typography>
        <Grid
          container
          spacing={2}
          m={0}
          mt={0}
          className="MuiGrid-root-width"
          sx={{ flexDirection: { xs: 'column', md: 'row' } }}
        >
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: {
                lg: 'left',
                md: 'left',
                sm: 'center',
                xs: 'center',
              },
            }}
          >
            <Box sx={{ mb: { xs: 3, md: 0 }, mt: { xs: -3, md: 0 } }}>
              <Typography component="div" variant="body1" sx={{ mb: 3 }}>
                If your vehicle weighs 55,000 pounds or more, you must file{' '}
                <span style={{ fontWeight: 600 }}>IRS Form 2290</span> and pay
                the{' '}
                <span style={{ fontWeight: 600 }}>
                  Heavy Vehicle Use Tax (HVUT)
                </span>
                .
              </Typography>
              <Typography variant="body1">
                📄 <span style={{ fontWeight: 600 }}>Need help?</span>{' '}
                <Link
                  href="/resources/excise-tax/form-2290-instructions/"
                  sx={{ color: '#0202ff' }}
                >
                  {' '}
                  Check out Form 2290 Instructions
                </Link>{' '}
              </Typography>

              <Box
                sx={{
                  width: 'auto',
                  textAlign: 'left',
                  alignItems: 'center',
                  display: 'inline-block',
                  mt: { xs: 2, sm: 2 },
                }}
              >
                <Button
                  variant="contained"
                  className="btn-primary medium"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=difference`}
                  sx={{
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': {
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  E-file Now
                </Button>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ md: 6, xs: 12 }} textAlign="right">
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Box
                component="iframe"
                title="What is Form 2290: A Comprehensive Guide"
                src="https://www.youtube.com/embed/QnUmGYD2yLw?&rel=0"
                sx={{
                  width: { xs: '100%', sm: 500 },
                  height: { xs: '100%', sm: 281 },
                  border: '10px solid #333 !important',
                  borderRadius: 5,
                }}
                allowFullScreen={true}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Difference;
